/**
 * Network monitoring utility
 * Monitors network conditions and adapts app behavior accordingly
 * Optimized to reduce network traffic and battery usage
 */

export interface NetworkStatus {
  online: boolean;
  effectiveType: 'slow-2g' | '2g' | '3g' | '4g' | 'unknown';
  downlink: number | null;
  rtt: number | null;
  saveData: boolean;
  isMobile: boolean;
}

// Default network status
const DEFAULT_STATUS: NetworkStatus = {
  online: true,
  effectiveType: 'unknown',
  downlink: null,
  rtt: null,
  saveData: false,
  isMobile: false
};

// Subscribers to network changes
type NetworkListener = (status: NetworkStatus) => void;
const listeners: NetworkListener[] = [];

// Current network status
let currentStatus: NetworkStatus = { ...DEFAULT_STATUS };

// RTT thresholds for network classification (ms)
const RTT_THRESHOLDS = {
  SLOW_2G: 1200,
  TWO_G: 800,
  THREE_G: 400,
  FOUR_G: 180
};

// Last known accurate network measurement
let lastAccurateRtt: number | null = null;
let lastMeasurementTime = 0;

// Monitor initialization flag to prevent duplicates
let monitorInitialized = false;

// Flag to prevent too frequent checks
let rttCheckInProgress = false;
// Cooldown flag to prevent excessive network checks
let networkCheckCooldown = false;

/**
 * Initialize network monitoring - with safeguards against multiple initializations
 */
export function initNetworkMonitoring(debug = false): boolean {
  // Skip if already initialized or in a server environment
  if (monitorInitialized || typeof window === 'undefined') return false;
  
  // Set initialization flag
  monitorInitialized = true;
  
  // Detect if we're on a mobile device
  currentStatus.isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  
  // Set initial online status
  currentStatus.online = navigator.onLine;
  
  // Use Network Information API if available
  if ('connection' in navigator) {
    const connection = (navigator as any).connection;
    
    if (connection) {
      updateFromConnection(connection);
      
      // Listen for connection changes but with throttling to avoid excessive updates
      let connectionChangeTimeout: number | null = null;
      
      connection.addEventListener('change', () => {
        // Clear any pending timeout
        if (connectionChangeTimeout !== null) {
          clearTimeout(connectionChangeTimeout);
        }
        
        // Throttle the updates to reduce overhead
        connectionChangeTimeout = window.setTimeout(() => {
          updateFromConnection(connection);
          notifyListeners();
          connectionChangeTimeout = null;
          if (debug) console.log(`Network changed: ${currentStatus.effectiveType}, ${currentStatus.downlink}Mbps, RTT: ${currentStatus.rtt}ms`);
        }, 2000); // 2-second throttle
      });
    }
  }
  
  // Listen for online/offline events
  window.addEventListener('online', () => {
    currentStatus.online = true;
    notifyListeners();
    console.log('Device is online');
    
    // When coming back online, check connection quality but with a delay
    setTimeout(() => {
      if (!lastAccurateRtt || Date.now() - lastMeasurementTime > 60000) {
        performRttCheck();
      }
    }, 5000); // Add a 5-second delay before checking RTT after coming online
  });
  
  window.addEventListener('offline', () => {
    currentStatus.online = false;
    notifyListeners();
    console.log('Device is offline');
  });
  
  // For slow connections, don't do any initial RTT checks
  // Just rely on the connection API information
  if (getCurrentConnectionType() !== 'slow-2g' && getCurrentConnectionType() !== '2g') {
    // Perform a simple initial RTT check after a delay
    setTimeout(() => {
      performInitialRttCheck();
    }, 5000); // Delay the initial check to prioritize app loading
  } else {
    console.log('Skipping initial RTT check due to detected slow connection');
  }
  
  return true;
}

/**
 * Get the current connection type from the Connection API without RTT checks
 */
function getCurrentConnectionType(): 'slow-2g' | '2g' | '3g' | '4g' | 'unknown' {
  if (typeof navigator === 'undefined' || !('connection' in navigator)) {
    return 'unknown';
  }
  
  const connection = (navigator as any).connection;
  if (!connection || !connection.effectiveType) {
    return 'unknown';
  }
  
  return connection.effectiveType;
}

/**
 * Perform initial RTT check to get accurate classification faster
 * Now with timeouts to prevent hanging
 */
async function performInitialRttCheck(): Promise<void> {
  try {
    // Skip if offline
    if (!currentStatus.online) return;
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10-second timeout
    
    const start = performance.now();
    // Use a very small file to test connection speed
    const response = await fetch('/favicon.ico', { 
      method: 'HEAD',
      cache: 'no-store',
      headers: { 'Cache-Control': 'no-cache' },
      signal: controller.signal
    });
    clearTimeout(timeoutId);
    
    const duration = performance.now() - start;
    
    // Only update if we got a successful response
    if (response.ok) {
      // Record this as an accurate RTT measurement
      lastAccurateRtt = duration;
      lastMeasurementTime = Date.now();
      
      // Update RTT and infer network type
      currentStatus.rtt = duration;
      
      // Use the adjusted RTT thresholds for classification
      classifyConnectionByRtt(duration);
      
      notifyListeners();
      console.log(`Initial network classification: ${currentStatus.effectiveType} (RTT: ${Math.round(duration)}ms)`);
    }
  } catch (error) {
    if ((error as any).name === 'AbortError') {
      console.warn('Initial RTT check timed out - assuming slow connection');
      // If timeout occurs, assume a very slow connection
      currentStatus.effectiveType = 'slow-2g';
    } else {
      console.warn('Initial RTT check failed:', error);
      // If check fails, assume moderate connection
      currentStatus.effectiveType = '3g';
    }
    notifyListeners();
  }
}

/**
 * Classify connection based on RTT value
 */
function classifyConnectionByRtt(rtt: number): void {
  if (rtt >= RTT_THRESHOLDS.SLOW_2G) {
    currentStatus.effectiveType = 'slow-2g';
  } else if (rtt >= RTT_THRESHOLDS.TWO_G) {
    currentStatus.effectiveType = '2g';
  } else if (rtt >= RTT_THRESHOLDS.THREE_G) {
    currentStatus.effectiveType = '3g';
  } else {
    currentStatus.effectiveType = '4g';
  }
}

/**
 * Update network status from Connection API
 */
function updateFromConnection(connection: any): void {
  // Store raw values from the connection API
  currentStatus.downlink = connection.downlink || null;
  currentStatus.rtt = connection.rtt || null;
  currentStatus.saveData = !!connection.saveData;
  
  // Get the reported effective type
  const reportedType = connection.effectiveType || 'unknown';
  
  // If we have a valid RTT measurement, use a blend of reported and measured data
  if (lastAccurateRtt !== null && lastMeasurementTime > Date.now() - 60000) {
    // For very high RTT, be more conservative
    if (lastAccurateRtt > RTT_THRESHOLDS.SLOW_2G && reportedType !== 'slow-2g') {
      currentStatus.effectiveType = getNextSlowerNetworkType(reportedType);
    } else {
      currentStatus.effectiveType = reportedType;
    }
  } else {
    // Without RTT, use reported type
    currentStatus.effectiveType = reportedType;
  }
}

/**
 * Get the next slower network type (one level down)
 */
function getNextSlowerNetworkType(currentType: string): 'slow-2g' | '2g' | '3g' | '4g' {
  switch (currentType) {
    case '4g': return '3g';
    case '3g': return '2g';
    case '2g': return 'slow-2g';
    default: return 'slow-2g';
  }
}

/**
 * Perform periodic network quality checks - with greatly reduced frequency
 */
function startPeriodicChecks(): void {
  // First check after page load - with a longer delay
  setTimeout(performNetworkCheck, 10000);
  
  // Then run checks much less frequently (reduced from 60s to 5 minutes)
  // This significantly reduces network traffic and battery usage
  setInterval(performNetworkCheck, 5 * 60 * 1000);
}

/**
 * Perform a lightweight network quality check
 * With added cooldown to prevent excessive checks
 */
async function performRttCheck(): Promise<number | null> {
  // Skip if offline
  if (!currentStatus.online) return null;
  
  // Skip if check already in progress
  if (rttCheckInProgress) return null;
  
  // Skip if on cooldown
  if (networkCheckCooldown) return null;
  
  // Set cooldown flag to prevent excessive checks
  networkCheckCooldown = true;
  setTimeout(() => {
    networkCheckCooldown = false;
  }, 30000); // 30 second cooldown
  
  // Set flag to prevent concurrent checks
  rttCheckInProgress = true;
  
  try {
    const start = performance.now();
    const response = await fetch('/favicon.ico', { 
      method: 'HEAD',
      cache: 'no-store',
      headers: { 'Cache-Control': 'no-cache' }
    });
    
    if (!response.ok) {
      rttCheckInProgress = false;
      return null;
    }
    
    const duration = performance.now() - start;
    
    // Record this measurement
    lastAccurateRtt = duration;
    lastMeasurementTime = Date.now();
    
    rttCheckInProgress = false;
    return duration;
  } catch (error) {
    rttCheckInProgress = false;
    return null;
  }
}

/**
 * Perform a complete network quality check
 */
async function performNetworkCheck(): Promise<void> {
  const rtt = await performRttCheck();
  
  if (rtt !== null) {
    // Update RTT based on the fetch duration
    currentStatus.rtt = rtt;
    
    // Classify connection using the measured RTT
    let previousType = currentStatus.effectiveType;
    classifyConnectionByRtt(rtt);
    
    // Only update if the connection type actually changed
    if (previousType !== currentStatus.effectiveType) {
      console.log(`Network quality updated from ${previousType} to ${currentStatus.effectiveType} (RTT: ${Math.round(rtt)}ms)`);
      notifyListeners();
    }
  }
}

/**
 * Notify all listeners of network status changes
 * With debouncing to prevent too many updates
 */
let notifyTimeout: number | null = null;
function notifyListeners(): void {
  // Clear any pending notification
  if (notifyTimeout !== null) {
    clearTimeout(notifyTimeout);
  }
  
  // Debounce notifications to reduce update frequency
  notifyTimeout = window.setTimeout(() => {
    listeners.forEach(listener => {
      try {
        listener({ ...currentStatus });
      } catch (error) {
        console.error('Error in network status listener:', error);
      }
    });
    notifyTimeout = null;
  }, 200); // 200ms debounce
}

/**
 * Subscribe to network status changes
 */
export function subscribeToNetworkChanges(listener: NetworkListener): () => void {
  listeners.push(listener);
  
  // Immediately notify the listener of the current status
  listener({ ...currentStatus });
  
  // Return unsubscribe function
  return () => {
    const index = listeners.indexOf(listener);
    if (index !== -1) {
      listeners.splice(index, 1);
    }
  };
}

/**
 * Get the current network status
 * Now with caching to reduce unnecessary object creation
 */
const cachedNetworkStatus = { ...currentStatus };
export function getNetworkStatus(): NetworkStatus {
  // Update the cached object
  Object.assign(cachedNetworkStatus, currentStatus);
  // Return the cached reference instead of creating a new object
  return cachedNetworkStatus;
}

/**
 * Check if the device is in low data mode
 */
export function isLowDataMode(): boolean {
  if (currentStatus.saveData) return true;
  
  // Consider slow connections as low data mode
  return (
    currentStatus.effectiveType === 'slow-2g' ||
    currentStatus.effectiveType === '2g'
  );
}

/**
 * Check if the device is on a slow connection
 */
export function isSlowConnection(): boolean {
  return (
    currentStatus.effectiveType === 'slow-2g' ||
    currentStatus.effectiveType === '2g' ||
    (currentStatus.effectiveType === '3g' && currentStatus.rtt !== null && currentStatus.rtt > 800)
  );
}

/**
 * Force a network classification refresh
 * Now with a timeout to prevent hanging
 */
export function refreshNetworkStatus(): Promise<NetworkStatus> {
  return new Promise((resolve) => {
    // Add a safety timeout
    const timeoutId = setTimeout(() => {
      console.warn('Network status refresh timed out');
      resolve({ ...currentStatus });
    }, 5000);
    
    performNetworkCheck().then(() => {
      clearTimeout(timeoutId);
      resolve({ ...currentStatus });
    }).catch(() => {
      clearTimeout(timeoutId);
      resolve({ ...currentStatus });
    });
  });
}
